import { api } from '@goaltree/backend/convex/_generated/api'
import { Checkbox } from '@goaltree/ui/components/checkbox'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { Star } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { UserAvatar } from '@/components/user-avatar'

// Admin moderation: only approved reviews with consent reach goaltree.ai.
export function AdminReviews() {
  const reviews = useQuery(api.reviews.listForAdmin)
  const setApproved = useMutation(api.reviews.setApproved)
  if (!reviews) return null
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground/40">
        Reviews · {reviews.length}
        {reviews.length > 0 && ` · ${avg.toFixed(1)}★ avg`}
      </h2>
      {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet.</p>}
      {reviews.map((r) => (
        <div
          key={r._id}
          className="flex flex-col gap-2 rounded-xl border border-border/60 bg-card/40 p-3"
        >
          <div className="flex items-center gap-2">
            {r.image && <img src={r.image} alt="" className="size-6 rounded-full object-cover" />}
            <span className="text-sm font-semibold text-foreground">{r.name}</span>
            <span className="flex">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={cn(
                    'size-3.5',
                    n <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30',
                  )}
                />
              ))}
            </span>
            <span className="ml-auto text-[11px] text-muted-foreground">
              {r.platform} · {new Date(r.updatedAt).toLocaleDateString()}
            </span>
          </div>
          {r.text && <p className="text-sm whitespace-pre-wrap text-foreground/80">{r.text}</p>}
          <div className="flex items-center gap-2">
            {r.consent ? (
              <button
                type="button"
                onClick={() =>
                  void setApproved({ reviewId: r._id, approved: !r.approved }).catch(() =>
                    toast.error('Could not update'),
                  )
                }
                className={cn(
                  'cursor-pointer rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                  r.approved
                    ? 'bg-primary/15 text-primary-text hover:bg-primary/25'
                    : 'bg-muted text-muted-foreground hover:text-foreground',
                )}
              >
                {r.approved ? 'Featured on site ✓' : 'Feature on site'}
              </button>
            ) : (
              <span className="text-xs text-muted-foreground">Private — no consent to feature</span>
            )}
          </div>
        </div>
      ))}
    </section>
  )
}

// Ask chosen friends for a review: the prompt opens on their next app visit.
export function AdminReviewRequests() {
  const people = useQuery(api.reviews.requestCandidates)
  const request = useMutation(api.reviews.requestReviews)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  if (!people || people.length === 0) return null
  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  async function send() {
    setBusy(true)
    try {
      const n = await request({ userIds: [...picked] })
      toast.success(`Asked ${n} ${n === 1 ? 'person' : 'people'} for a review`)
      setPicked(new Set())
    } catch {
      toast.error('Could not send requests')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="flex items-center justify-between text-xs font-semibold uppercase tracking-widest text-foreground/40">
        Ask friends for a review
        <button
          type="button"
          disabled={!picked.size || busy}
          onClick={send}
          className="cursor-pointer rounded-full bg-primary px-3 py-1 text-xs font-bold normal-case tracking-normal text-primary-foreground transition-opacity disabled:cursor-default disabled:opacity-40"
        >
          Ask {picked.size || ''}
        </button>
      </h2>
      <div className="flex flex-col divide-y divide-border/50 rounded-xl border border-border/60 bg-card/40">
        {people.map((p) => {
          const status = p.optedOut
            ? "Said don't ask"
            : p.requested
              ? 'Asked — waiting'
              : p.reviewed && !p.privateReview
                ? 'Reviewed'
                : null
          const note = !status && p.privateReview ? 'Private review — ask to share' : null
          return (
            <label
              key={p.userId}
              className={cn(
                'flex items-center gap-3 px-3 py-2',
                status ? 'opacity-60' : 'cursor-pointer hover:bg-muted/40',
              )}
            >
              <Checkbox
                checked={picked.has(p.userId)}
                disabled={!!status}
                onCheckedChange={() => toggle(p.userId)}
              />
              <UserAvatar
                image={p.image}
                name={p.name}
                className="size-6 rounded-full object-cover"
              />
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                {p.name}
                {p.handle && <span className="text-muted-foreground"> @{p.handle}</span>}
              </span>
              {p.joinedAt > 0 && (
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                  {new Date(p.joinedAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: '2-digit',
                  })}
                </span>
              )}
              {(status ?? note) && (
                <span className="shrink-0 text-[11px] text-muted-foreground">{status ?? note}</span>
              )}
            </label>
          )
        })}
      </div>
    </section>
  )
}
